import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Button,
  Grid as MuiGrid,
  Divider,
  Chip,
  LinearProgress,
  Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import EqualizerIcon from '@mui/icons-material/Equalizer';

// Sample bot data - in a real app, this would come from your API
const tradingBots = [
  {
    id: 'bot1',
    name: 'XAU Trend Finder',
    description: 'Uses momentum indicators to identify trending markets in Gold',
    monthlyReturn: 8.2,
    yearToDateReturn: 24.5,
    trades: 34,
    winRate: 68,
    status: 'active',
    strategy: 'Momentum',
    timeframe: '4H',
    color: '#FFD700', // Gold color
    icon: <TrendingUpIcon />
  },
  {
    id: 'bot2',
    name: 'USD Reversal',
    description: 'Identifies key USD reversal points using multiple timeframe analysis',
    monthlyReturn: 6.7,
    yearToDateReturn: 19.8,
    trades: 22,
    winRate: 72,
    status: 'active',
    strategy: 'Mean Reversion',
    timeframe: '1H',
    color: '#87CEEB', // Light blue
    icon: <AutorenewIcon />
  },
  {
    id: 'bot3',
    name: 'Gold Volatility Harvester',
    description: 'Capitalizes on volatile gold price movements using breakout strategies',
    monthlyReturn: 9.6,
    yearToDateReturn: 31.2,
    trades: 42,
    winRate: 64,
    status: 'active',
    strategy: 'Breakout',
    timeframe: '1D',
    color: '#FF8C00', // Dark orange
    icon: <EqualizerIcon />
  }
];

// Styled components
const BotCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[10],
  }
}));

const ReturnChip = styled(Chip)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: theme.palette.success.light,
  color: theme.palette.success.contrastText,
}));

const TradingBotsSection: React.FC = () => {
  return (
    <Box component="section" sx={{ mb: 6 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom color="secondary.main">
          Trading Bots Performance
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
          Our algorithmic trading bots continuously monitor Gold and USD markets, implementing proven strategies with real-time execution.
        </Typography>
      </Box>

      <MuiGrid container spacing={3}>
        {tradingBots.map((bot) => (
          <MuiGrid item key={bot.id} xs={12} sm={6} md={4}>
            <BotCard>
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: bot.color }}>
                    {bot.icon}
                  </Avatar>
                }
                title={bot.name}
                subheader={`Strategy: ${bot.strategy} | ${bot.timeframe}`}
                action={
                  <Chip 
                    size="small" 
                    label="Active" 
                    color="success" 
                    sx={{ mt: 1 }}
                  />
                }
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {bot.description}
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="body2">Monthly Return</Typography>
                    <ReturnChip size="small" label={`+${bot.monthlyReturn}%`} />
                  </Stack>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min(bot.monthlyReturn * 5, 100)}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="body2">YTD Return</Typography>
                    <ReturnChip size="small" label={`+${bot.yearToDateReturn}%`} />
                  </Stack>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min(bot.yearToDateReturn * 2, 100)}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">
                    Trades: {bot.trades}
                  </Typography>
                  <Typography variant="body2">
                    Win Rate: {bot.winRate}%
                  </Typography>
                </Stack>
              </CardContent>
              <Box sx={{ p: 2, pt: 0 }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  fullWidth
                  startIcon={<ContentCopyIcon />}
                  sx={{ color: 'secondary.main' }}
                >
                  Copy on IC Markets
                </Button>
              </Box>
            </BotCard>
          </MuiGrid>
        ))}
      </MuiGrid>

      <Box sx={{ mt: 4, textAlign: 'center', bgcolor: 'background.paper', p: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Copy Trading Now Available
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          You can now follow our trading bots' performance and copy their trades directly through IC Markets.
          Start with as little as $100 and automatically replicate our bots' trades in your own account.
        </Typography>
        <Button 
          variant="outlined" 
          color="secondary"
          sx={{ mt: 1 }}
        >
          Learn How to Copy Trade
        </Button>
      </Box>
    </Box>
  );
};

export default TradingBotsSection;